import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faChevronLeft, faChevronRight, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FaComments, FaWhatsapp, FaTimes, FaPhone } from 'react-icons/fa';
import { faFacebookMessenger } from '@fortawesome/free-brands-svg-icons';
import ProductCard from './ProductCard';
import ChatScreen from './ChatScreen';

/* ─────────────────────────────────────────
   Fresh Food Color Palette
   Russian Red:     #CD0302  → alerts, danger, badges
   Sunburst:        #F0C400  → coins, highlights
   Edam:            #F5D19D  → borders, warm light bg
   Citrusy Orange:  #F88702  → secondary accent, hover
   Autumn Green:    #4F7E17  → primary brand, CTA
   Warm light bg:   #FFF8EC  → derived from Edam
───────────────────────────────────────── */

// ── Collapsible Section ──────────────────
const SidebarSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #F5D19D', paddingBottom: open ? '8px' : '0' }}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontWeight: 700,
          fontSize: '13px',
          color: '#333',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}
      >
        <span>{title}</span>
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          style={{ fontSize: '11px', color: '#4F7E17', transition: 'transform 0.2s' }}
        />
      </button>
      {open && <div style={{ padding: '0 8px 8px 8px' }}>{children}</div>}
    </div>
  );
};

// ── Nav Item (category row) ──────────────
const NavItem = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '7px 10px',
      borderRadius: '6px',
      background: active ? '#edf5e0' : 'transparent',
      border: 'none',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background 0.15s',
      marginBottom: '1px',
    }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#FFF8EC'; }}
    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
  >
    <span style={{
      fontSize: '13px',
      color: active ? '#4F7E17' : '#444',
      fontWeight: active ? 700 : 400,
      lineHeight: '1.3',
    }}>
      {active && (
        <span style={{
          display: 'inline-block',
          width: '3px',
          height: '13px',
          background: '#4F7E17',
          borderRadius: '2px',
          marginRight: '8px',
          verticalAlign: 'middle',
        }} />
      )}
      {label}
    </span>
    {count !== undefined && (
      <span style={{
        fontSize: '11px',
        color: active ? '#4F7E17' : '#aaa',
        fontWeight: active ? 700 : 400,
      }}>
        {count}
      </span>
    )}
  </button>
);

// ── Filter Sidebar ───────────────────────
const FilterContent = ({
  isMobile,
  searchQuery, setSearchQuery,
  minPrice, setMinPrice,
  maxPrice, setMaxPrice,
  priceRange, setPriceRange,
  selectedMainCategory, setSelectedMainCategory,
  selectedSubCategory, setSelectedSubCategory,
  apiMainCategories, apiSubCategories,
  categories, selectedCategories, handleCategoryChange,
  sortOption, setSortOption,
  handleApplyFilters, handleClearFilters,
  setIsMobileFilterOpen,
}) => {
  const sortOptions = [
    { value: '', label: 'Relevance' },
    { value: 'low_to_high', label: 'Price: Low → High' },
    { value: 'high_to_low', label: 'Price: High → Low' },
    { value: 'a_z', label: 'Name: A → Z' },
    { value: 'z_a', label: 'Name: Z → A' },
    { value: 'newest', label: 'Newest' },
  ];

  return (
    <div style={{
      background: '#fff',
      borderRadius: isMobile ? '0' : '8px',
      border: isMobile ? 'none' : '1px solid #F5D19D',
      overflow: 'hidden',
      fontFamily: "'Segoe UI', Helvetica, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: '#4F7E17',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px', letterSpacing: '0.03em' }}>
          🗂 Categories & Filters
        </span>
        {isMobile && (
          <button onClick={() => setIsMobileFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', fontSize: '18px' }}>
            <FaTimes />
          </button>
        )}
      </div>

      <div style={{ overflowY: 'auto', maxHeight: isMobile ? 'calc(100vh - 160px)' : 'calc(100vh - 100px)' }}>

        {/* Search */}
        <SidebarSection title="Search" defaultOpen={true}>
          <input
            type="search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search products..."
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1.5px solid #F5D19D',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#333',
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
            onFocus={e => e.target.style.borderColor = '#4F7E17'}
            onBlur={e => e.target.style.borderColor = '#F5D19D'}
          />
        </SidebarSection>

        {/* Main Category */}
        {apiMainCategories.length > 0 && (
          <SidebarSection title="Main Category" defaultOpen={true}>
            <NavItem
              label="All"
              active={!selectedMainCategory}
              onClick={() => setSelectedMainCategory('')}
            />
            {apiMainCategories.map(cat => (
              <NavItem
                key={cat.id}
                label={cat.name}
                active={selectedMainCategory === cat.id}
                onClick={() => setSelectedMainCategory(cat.id)}
              />
            ))}
          </SidebarSection>
        )}

        {/* Sub Category */}
        {apiSubCategories.length > 0 && (
          <SidebarSection title="Sub Category" defaultOpen={false}>
            <NavItem
              label="All"
              active={!selectedSubCategory}
              onClick={() => setSelectedSubCategory('')}
            />
            {apiSubCategories.map(cat => (
              <NavItem
                key={cat.id}
                label={cat.name}
                active={selectedSubCategory === cat.id}
                onClick={() => setSelectedSubCategory(cat.id)}
              />
            ))}
          </SidebarSection>
        )}

        {/* Tags */}
        <SidebarSection title="Filter by Tag" defaultOpen={false}>
          {categories.map(category => (
            <label
              key={category}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                borderRadius: '6px',
                cursor: 'pointer',
                marginBottom: '1px',
                background: selectedCategories.includes(category) ? '#edf5e0' : 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (!selectedCategories.includes(category)) e.currentTarget.style.background = '#FFF8EC'; }}
              onMouseLeave={e => { if (!selectedCategories.includes(category)) e.currentTarget.style.background = 'transparent'; }}
            >
              <input
                type="checkbox"
                value={category}
                checked={selectedCategories.includes(category)}
                onChange={handleCategoryChange}
                style={{ accentColor: '#4F7E17', width: '14px', height: '14px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '13px', color: selectedCategories.includes(category) ? '#4F7E17' : '#444', fontWeight: selectedCategories.includes(category) ? 600 : 400 }}>
                {category}
              </span>
            </label>
          ))}
        </SidebarSection>

        {/* Price Range */}
        <SidebarSection title="Price Range" defaultOpen={false}>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
            <input
              type="number"
              value={minPrice}
              onChange={e => setMinPrice(Number(e.target.value))}
              placeholder="Min"
              style={{
                width: '50%', padding: '7px 10px', border: '1.5px solid #F5D19D',
                borderRadius: '6px', fontSize: '12px', outline: 'none', boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = '#4F7E17'}
              onBlur={e => e.target.style.borderColor = '#F5D19D'}
            />
            <input
              type="number"
              value={maxPrice}
              onChange={e => setMaxPrice(Number(e.target.value))}
              placeholder="Max"
              style={{
                width: '50%', padding: '7px 10px', border: '1.5px solid #F5D19D',
                borderRadius: '6px', fontSize: '12px', outline: 'none', boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = '#4F7E17'}
              onBlur={e => e.target.style.borderColor = '#F5D19D'}
            />
          </div>
          <input
            type="range"
            min="0"
            max="200000"
            value={priceRange[1]}
            onChange={e => {
              const v = parseInt(e.target.value);
              setPriceRange([0, v]);
              setMaxPrice(v);
            }}
            style={{
              width: '100%',
              accentColor: '#4F7E17',
              background: `linear-gradient(to right, #4F7E17 0%, #4F7E17 ${(priceRange[1] / 200000) * 100}%, #F5D19D ${(priceRange[1] / 200000) * 100}%, #F5D19D 100%)`,
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#888', marginTop: '4px' }}>
            <span>৳{minPrice.toLocaleString()}</span>
            <span>৳{maxPrice.toLocaleString()}</span>
          </div>
        </SidebarSection>

        {/* Sort */}
        <SidebarSection title="Sort By" defaultOpen={false}>
          {sortOptions.map(opt => (
            <NavItem
              key={opt.value}
              label={opt.label}
              active={sortOption === opt.value}
              onClick={() => setSortOption(opt.value)}
            />
          ))}
        </SidebarSection>

      </div>

      {/* Actions */}
      <div style={{ padding: '12px', borderTop: '1px solid #F5D19D', background: '#fff' }}>
        <button
          onClick={handleApplyFilters}
          style={{
            width: '100%', padding: '10px', borderRadius: '6px',
            background: '#4F7E17', color: '#fff', border: 'none',
            fontWeight: 700, fontSize: '13px', cursor: 'pointer', marginBottom: '6px',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#3a5e11'}
          onMouseLeave={e => e.currentTarget.style.background = '#4F7E17'}
        >
          Apply Filters
        </button>
        <button
          onClick={handleClearFilters}
          style={{
            width: '100%', padding: '9px', borderRadius: '6px',
            background: '#fff', color: '#F88702', border: '1.5px solid #F88702',
            fontWeight: 600, fontSize: '13px', cursor: 'pointer',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────
const AllProduct = ({
  products,
  addToCart,
  toggleFavorite,
  isFavorite,
  cartItems,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 200000]);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(200000);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(false);
  const [showButtonText, setShowButtonText] = useState(true);
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [apiMainCategories, setApiMainCategories] = useState([]);
  const [apiSubCategories, setApiSubCategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState(null);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [apiCategoriesWithId, setApiCategoriesWithId] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);

  const whatsAppPhoneNumber = '8801345688562';
  const whatsAppDefaultMessage = 'Hello! I visited your website and would like some information.';
  const defaultCategories = ['Gadget', 'Toys', 'Home Accessories', 'PC Component', 'Gym', 'Smart Watch', 'Light'];
  const BASE_URL = 'https://bazar-wala-main-server-974485829900.asia-south1.run.app';

  const fetchProductsByCategory = async (categoryId) => {
    setIsCategoryLoading(true);
    try {
      const url = `${BASE_URL}/api/products/category/${encodeURIComponent(categoryId)}?per_page=50`;
      const response = await fetch(url);
      const data = await response.json();
      setCategoryProducts(data.products?.length > 0 ? data.products : null);
    } catch (error) {
      console.error('Category fetch error:', error);
      setCategoryProducts(null);
    } finally {
      setIsCategoryLoading(false);
    }
  };

  const handleMainCategoryChange = async (value) => {
    setSelectedMainCategory(value);
    setSelectedSubCategory('');
    setSelectedCategories([]);
    if (!value) { setCategoryProducts(null); return; }
    await fetchProductsByCategory(value);
  };

  const handleSubCategoryChange = async (value) => {
    setSelectedSubCategory(value);
    setSelectedCategories([]);
    if (!value) {
      if (selectedMainCategory) await fetchProductsByCategory(selectedMainCategory);
      else setCategoryProducts(null);
      return;
    }
    await fetchProductsByCategory(value);
  };

  const handleCategoryChange = async (event) => {
    const { value, checked } = event.target;
    let newCats = checked
      ? [...selectedCategories, value]
      : selectedCategories.filter(c => c !== value);
    setSelectedCategories(newCats);
    setSelectedMainCategory('');
    setSelectedSubCategory('');
    if (newCats.length === 1) {
      const found = apiCategoriesWithId.find(c => c.name === newCats[0]);
      await fetchProductsByCategory(found?.id || newCats[0]);
    } else if (newCats.length === 0) {
      setCategoryProducts(null);
    } else {
      setCategoryProducts(null);
    }
  };

  const handleApplyFilters = () => {
    if (isMobileFilterOpen) setIsMobileFilterOpen(false);
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setPriceRange([0, 200000]);
    setMinPrice(0);
    setMaxPrice(200000);
    setSearchQuery('');
    setSortOption('');
    setSelectedMainCategory('');
    setSelectedSubCategory('');
    setCategoryProducts(null);
  };

  useEffect(() => {
    const categoryParam = searchParams.get('c');
    if (!categoryParam) return;
    if (!apiCategoriesWithId.length && !apiMainCategories.length && !apiSubCategories.length) return;

    const fetchOnLoad = async () => {
      const matchInMain = apiMainCategories.find(c => c.id === categoryParam || c.name.toLowerCase() === categoryParam.toLowerCase());
      const matchInSub = apiSubCategories.find(c => c.id === categoryParam || c.name.toLowerCase() === categoryParam.toLowerCase());
      const matchInTag = apiCategoriesWithId.find(c => c.id === categoryParam || c.name.toLowerCase() === categoryParam.toLowerCase());
      const matched = matchInTag || matchInMain || matchInSub;
      const categoryId = matched?.id || categoryParam;
      if (matchInMain) { setSelectedMainCategory(matchInMain.id); setSelectedSubCategory(''); }
      else if (matchInSub) { setSelectedSubCategory(matchInSub.id); }
      else if (matchInTag) { setSelectedCategories([matchInTag.name]); }
      await fetchProductsByCategory(categoryId);
    };
    fetchOnLoad();
  }, [searchParams, apiCategoriesWithId, apiMainCategories, apiSubCategories]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/categories`);
        const data = await response.json();
        if (data.success && data.categories?.length > 0) {
          const active = data.categories.filter(c => c.isActive);
          const tagCats = active.filter(c => c.level === 'category').map(c => ({ id: c.id, name: c.name }));
          setApiCategoriesWithId(tagCats);
          setApiCategories(tagCats.map(c => c.name));
          setApiMainCategories(active.filter(c => c.level === 'main').map(c => ({ id: c.id, name: c.name })));
          setApiSubCategories(active.filter(c => c.level === 'subcategory').map(c => ({ id: c.id, name: c.name })));
        }
      } catch (e) { console.error('Failed to fetch categories:', e); }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowButtonText(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileFilterOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileFilterOpen]);

  const categories = apiCategories.length > 0 ? apiCategories : defaultCategories;

  const filteredProducts = useMemo(() => {
    const source = categoryProducts ?? products;
    let filtered = source.filter(p => {
      const matchSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || (p.description?.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchCat = categoryProducts !== null ? true : selectedCategories.length === 0 || selectedCategories.includes(p.category);
      const matchPrice = p.price >= minPrice && p.price <= maxPrice;
      return matchSearch && matchCat && matchPrice;
    });
    if (sortOption) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortOption) {
          case 'low_to_high': return a.price - b.price;
          case 'high_to_low': return b.price - a.price;
          case 'a_z': return a.name.localeCompare(b.name);
          case 'z_a': return b.name.localeCompare(a.name);
          case 'newest': return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          default: return 0;
        }
      });
    }
    return filtered;
  }, [products, categoryProducts, selectedCategories, minPrice, maxPrice, searchQuery, sortOption]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) { for (let i = 1; i <= 4; i++) pages.push(i); pages.push('...'); pages.push(totalPages); }
      else if (currentPage >= totalPages - 2) { pages.push(1); pages.push('...'); for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i); }
      else { pages.push(1); pages.push('...'); for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i); pages.push('...'); pages.push(totalPages); }
    }
    return pages;
  };

  const filterProps = {
    searchQuery, setSearchQuery,
    minPrice, setMinPrice,
    maxPrice, setMaxPrice,
    priceRange, setPriceRange,
    selectedMainCategory, setSelectedMainCategory: handleMainCategoryChange,
    selectedSubCategory, setSelectedSubCategory: handleSubCategoryChange,
    apiMainCategories, apiSubCategories,
    categories, selectedCategories, handleCategoryChange,
    sortOption, setSortOption,
    handleApplyFilters, handleClearFilters,
    setIsMobileFilterOpen,
  };

  const activeFilterCount = selectedCategories.length
    + (priceRange[1] !== 200000 ? 1 : 0)
    + (selectedMainCategory ? 1 : 0)
    + (selectedSubCategory ? 1 : 0)
    + (searchQuery ? 1 : 0);

  return (
    <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]" style={{ fontFamily: "'Segoe UI', Helvetica, sans-serif" }}>

      {/* Mobile Fixed Left Filter Tab */}
      <div
        className="md:hidden"
        style={{
          position: 'fixed',
          left: 0,
          top: '45%',
          transform: 'translateY(-50%)',
          zIndex: 998,
        }}
      >
        <button
          onClick={() => setIsMobileFilterOpen(true)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            padding: '12px 8px',
            background: '#4F7E17',
            border: 'none',
            borderRadius: '0 8px 8px 0',
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '2px 2px 10px rgba(79,126,23,0.35)',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#3a5e11'}
          onMouseLeave={e => e.currentTarget.style.background = '#4F7E17'}
        >
          <FontAwesomeIcon icon={faFilter} style={{ fontSize: '14px' }} />
          <span style={{
            fontSize: '10px',
            fontWeight: 700,
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            letterSpacing: '0.05em',
          }}>
            Filter
          </span>
          {activeFilterCount > 0 && (
            <span style={{
              background: '#F0C400',
              color: '#333',
              borderRadius: '50%',
              fontSize: '10px',
              fontWeight: 800,
              width: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Mobile Filter Drawer */}
      {isMobileFilterOpen && (
        <>
          <div
            className="fixed inset-0 z-[1001] md:hidden"
            style={{ background: 'rgba(0,0,0,0.45)' }}
            onClick={() => setIsMobileFilterOpen(false)}
          />
          <div
            className="fixed left-0 top-0 h-full z-[1002] md:hidden"
            style={{
              width: '85%',
              maxWidth: '320px',
              background: '#fff',
              boxShadow: '4px 0 24px rgba(79,126,23,0.15)',
              transform: isMobileFilterOpen ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.3s ease',
            }}
          >
            <FilterContent isMobile={true} {...filterProps} />
          </div>
        </>
      )}

      {/* Main Layout */}
      <div style={{ display: 'flex', gap: '16px', padding: '16px 24px', alignItems: 'flex-start' }}>

        {/* Desktop Sidebar */}
        <aside
          className="hidden md:block"
          style={{ width: '220px', flexShrink: 0, position: 'sticky', top: '16px' }}
        >
          <FilterContent isMobile={false} {...filterProps} />
        </aside>

        {/* Product Grid */}
        <main style={{ flex: 1, minWidth: 0 }}>
          {isCategoryLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
              <div style={{ color: '#4F7E17', fontSize: '14px', fontWeight: 600 }}>Loading...</div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div style={{
              background: '#fff', border: '1px solid #F5D19D', borderRadius: '10px',
              padding: '60px 24px', textAlign: 'center',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>No Products Found</h3>
              <p style={{ color: '#888', fontSize: '13px', marginBottom: '16px' }}>Try adjusting your filters.</p>
              <button
                onClick={handleClearFilters}
                style={{
                  background: '#4F7E17', color: '#fff', border: 'none', borderRadius: '6px',
                  padding: '10px 24px', fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ fontSize: '13px', color: '#888' }}>
                  <span style={{ fontWeight: 700, color: '#333' }}>{filteredProducts.length}</span> products found
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 pb-6">
                {filteredProducts.map(product => (
                  <div key={product.id} style={{ position: 'relative' }}>
                    {product.PCW === 0 && (
                      <div style={{
                        position: 'absolute', top: 0, right: 0, zIndex: 10,
                        background: '#4F7E17', color: '#fff', fontSize: '10px',
                        fontWeight: 700, padding: '3px 8px', borderRadius: '0 4px 0 6px',
                        boxShadow: '0 1px 4px rgba(79,126,23,0.3)',
                      }}>
                        Free Delivery
                      </div>
                    )}
                    <ProductCard
                      product={product}
                      addToCart={addToCart}
                      toggleFavorite={toggleFavorite}
                      isFavorite={isFavorite}
                      cartItems={cartItems}
                      navigate={navigate}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && onPageChange && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '32px', marginBottom: '48px' }}>
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 16px', border: '1.5px solid #F5D19D', borderRadius: '6px',
                  background: '#fff', color: '#333', fontWeight: 600, cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px',
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                <span className="hidden sm:inline">Prev</span>
              </button>

              {getPageNumbers().map((num, i) => (
                <React.Fragment key={i}>
                  {num === '...' ? (
                    <span style={{ padding: '8px 4px', color: '#aaa' }}>...</span>
                  ) : (
                    <button
                      onClick={() => onPageChange(num)}
                      style={{
                        minWidth: '38px', height: '38px', padding: '0 8px',
                        border: '1.5px solid', borderRadius: '6px', fontWeight: 600, fontSize: '13px',
                        cursor: 'pointer', transition: 'all 0.15s',
                        background: num === currentPage ? '#4F7E17' : '#fff',
                        color: num === currentPage ? '#fff' : '#333',
                        borderColor: num === currentPage ? '#4F7E17' : '#F5D19D',
                      }}
                    >
                      {num}
                    </button>
                  )}
                </React.Fragment>
              ))}

              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 16px', border: '1.5px solid #F5D19D', borderRadius: '6px',
                  background: '#fff', color: '#333', fontWeight: 600, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.4 : 1, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px',
                }}
              >
                <span className="hidden sm:inline">Next</span>
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </main>
      </div>

      {/* Floating Chat Buttons */}
      <div style={{ position: 'fixed', bottom: '80px', right: '24px', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
        {(isOptionsMenuOpen) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '4px' }}>
            <button
              onClick={() => { window.open(`https://wa.me/${whatsAppPhoneNumber}?text=${encodeURIComponent(whatsAppDefaultMessage)}`, '_blank'); setIsOptionsMenuOpen(false); }}
              title="WhatsApp"
              style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#25D366', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.15)', transition: 'transform 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <FaWhatsapp style={{ color: '#fff', fontSize: '22px' }} />
            </button>
            <button
              onClick={() => { window.open('https://m.me/elevateemart', '_blank'); setIsOptionsMenuOpen(false); }}
              title="Messenger"
              style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#0084FF', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.15)', transition: 'transform 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <FontAwesomeIcon icon={faFacebookMessenger} style={{ color: '#fff', fontSize: '22px' }} />
            </button>
            <button
              onClick={() => { window.location.href = 'tel:+8809647001250'; setIsOptionsMenuOpen(false); }}
              title="Call Us"
              style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#34B7F1', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.15)', transition: 'transform 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <FaPhone style={{ color: '#fff', fontSize: '18px' }} />
            </button>
          </div>
        )}

        {showButtonText && !isOptionsMenuOpen && (
          <div className="md:hidden" style={{ background: '#F88702', color: '#fff', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, boxShadow: '0 2px 8px rgba(248,135,2,0.3)' }}>
            Chat with us!
          </div>
        )}

        <button
          onClick={() => { setIsOptionsMenuOpen(p => !p); setIsChatPopupOpen(false); }}
          className="md:hidden"
          style={{
            width: '52px', height: '52px', borderRadius: '50%',
            background: '#4F7E17', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(79,126,23,0.4)',
            transform: isOptionsMenuOpen ? 'rotate(45deg)' : 'rotate(0)',
            transition: 'transform 0.25s ease',
          }}
        >
          {isOptionsMenuOpen
            ? <FaTimes style={{ color: '#fff', fontSize: '20px' }} />
            : <FaComments style={{ color: '#fff', fontSize: '20px' }} />
          }
        </button>
      </div>

      {/* Chat Popup */}
      <div style={{
        position: 'fixed', right: '24px', width: '90%', maxWidth: '350px',
        height: '600px', background: '#fff', borderRadius: '14px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', zIndex: 999,
        transition: 'bottom 0.4s cubic-bezier(0.34,1.56,0.64,1)',
        bottom: isChatPopupOpen ? '96px' : '-650px',
      }}>
        <ChatScreen onClose={() => setIsChatPopupOpen(false)} />
      </div>

      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none; width: 16px; height: 16px;
          border-radius: 50%; background: #4F7E17; cursor: pointer;
          border: 2px solid #fff; box-shadow: 0 1px 4px rgba(79,126,23,0.4);
        }
        input[type="range"]::-moz-range-thumb {
          width: 16px; height: 16px; border-radius: 50%;
          background: #4F7E17; cursor: pointer; border: 2px solid #fff;
        }
      `}</style>
    </div>
  );
};

export default AllProduct;